/**
 * @fileoverview ImpresionTablaRepository - Repositorio especializado para impresion_tabla
 */

import { DataRepository } from './base/DataRepository';
import type { 
  ImpresionTabla, 
  ImpresionTablaExpanded,
  CreateImpresionTablaData, 
  UpdateImpresionTablaData 
} from '../types/collections/ImpresionTabla';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from '../types/common/PocketBaseTypes';
import { createModuleLogger } from '../utils/logger';

/**
 * Repositorio especializado para la gestión de impresion_tabla
 * Extiende DataRepository con métodos específicos del dominio
 */
export class ImpresionTablaRepository extends DataRepository<ImpresionTabla> {
  private logger = createModuleLogger('ImpresionTablaRepository')
  
  constructor() {
    super('impresion_tabla');
  }

  /**
   * Obtiene una impresion_tabla con todas sus relaciones expandidas
   */
  async getWithAllRelations(id: string): Promise<ImpresionTablaExpanded> {
    try {
      const result = await this.pb.collection(this.collectionName)
        .getOne<ImpresionTablaExpanded>(id, {
          expand: 'parametros_tinta,parametros_tinta.tinta',
        });

      this.logger.debug('ImpresionTabla obtenida con relaciones', { id });
      return result;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener impresion_tabla ${id} con relaciones: ${error instanceof Error ? error.message : String(error)}`,
        'GET_WITH_RELATIONS_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene todas las impresion_tabla con relaciones expandidas
   */
  async getAllWithExpand(): Promise<ImpresionTablaExpanded[]> {
    try {
      const result = await this.pb.collection(this.collectionName)
        .getList<ImpresionTablaExpanded>(1, 500, {
          expand: 'parametros_tinta,parametros_tinta.tinta',
          sort: '-created'
        });

      this.logger.debug('ImpresionTabla obtenidas con relaciones', { count: result.items.length });
      return result.items;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener impresion_tabla con relaciones: ${error instanceof Error ? error.message : String(error)}`,
        'GET_ALL_WITH_EXPAND_ERROR',
        error
      );
    }
  }

  /**
   * Crea una nueva impresion_tabla con validaciones de negocio
   */
  async createWithValidation(data: CreateImpresionTablaData): Promise<ImpresionTabla> {
    try {
      // Validar que si hay parametros_tinta, sean válidos
      if (data.parametros_tinta && data.parametros_tinta.length > 0) {
        for (const parametroId of data.parametros_tinta) {
          try {
            await this.pb.collection('parametros_tinta').getOne(parametroId);
          } catch (error) {
            throw new RepositoryError(
              `Parámetro de tinta inválido: ${parametroId}`,
              'INVALID_PARAMETRO_TINTA',
              error
            );
          }
        }
      }

      // Calcular total automáticamente si no se proporciona
      if (data.impresos !== undefined && data.sobrantes !== undefined && data.total === undefined) {
        data.total = (data.impresos || 0) + (data.sobrantes || 0);
      }

      const result = await this.create(data);
      this.logger.debug('ImpresionTabla creada exitosamente', { id: result.id });
      return result;
    } catch (error) {
      if (error instanceof RepositoryError) {
        throw error;
      }
      throw new RepositoryError(
        `Error al crear impresion_tabla: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_ERROR',
        error
      );
    }
  }

  /**
   * Actualiza una impresion_tabla existente con validaciones de negocio
   */
  async updateWithValidation(id: string, data: UpdateImpresionTablaData): Promise<ImpresionTabla> {
    try {
      // Validar que si hay parametros_tinta, sean válidos
      if (data.parametros_tinta && data.parametros_tinta.length > 0) {
        for (const parametroId of data.parametros_tinta) {
          try {
            await this.pb.collection('parametros_tinta').getOne(parametroId);
          } catch (error) {
            throw new RepositoryError(
              `Parámetro de tinta inválido: ${parametroId}`,
              'INVALID_PARAMETRO_TINTA',
              error
            );
          }
        }
      }

      // Calcular total automáticamente si se actualizan impresos o sobrantes
      if (data.impresos !== undefined || data.sobrantes !== undefined) {
        const current = await this.getById(id);
        const impresos = data.impresos !== undefined ? data.impresos : current.impresos || 0;
        const sobrantes = data.sobrantes !== undefined ? data.sobrantes : current.sobrantes || 0;
        data.total = impresos + sobrantes;
      }

      const result = await this.update(id, data);
      this.logger.debug('ImpresionTabla actualizada exitosamente', { id });
      return result;
    } catch (error) {
      if (error instanceof RepositoryError) {
        throw error;
      }
      throw new RepositoryError(
        `Error al actualizar impresion_tabla ${id}: ${error instanceof Error ? error.message : String(error)}`,
        'UPDATE_ERROR',
        error
      );
    }
  }

  /**
   * Obtiene impresion_tabla por parámetros de tinta
   */
  async getByParametrosTinta(parametrosTintaIds: string[]): Promise<ImpresionTabla[]> {
    try {
      const filter = parametrosTintaIds.map(id => `parametros_tinta ~ "${id}"`).join(' || ');
      const result = await this.pb.collection(this.collectionName)
        .getList<ImpresionTabla>(1, 500, {
          filter,
          expand: 'parametros_tinta,parametros_tinta.tinta',
          sort: '-created'
        });

      this.logger.debug('ImpresionTabla obtenidas por parámetros de tinta', { 
        parametrosTintaIds, 
        count: result.items.length 
      });
      return result.items;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener impresion_tabla por parámetros de tinta: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_PARAMETROS_TINTA_ERROR',
        error
      );
    }
  }
}











